import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const ChatInterface = ({ messages, onSendMessage, loading, userRole }) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (inputValue?.trim() && !loading) {
      onSendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleActionClick = (action) => {
    if (action?.route) {
      navigate(action?.route);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Sparkles" size={32} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Welcome to AI Help!</h3>
            <p className="text-sm text-muted-foreground mb-4">
              I'm here to help you navigate TrainaryGym. Ask me anything!
            </p>
            <div className="space-y-2">
              <button
                onClick={() => onSendMessage('How do I check in a member?')}
                className="w-full text-left px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm text-foreground transition-colors"
              >
                How do I check in a member?
              </button>
              <button
                onClick={() => onSendMessage('How do I process a POS transaction?')}
                className="w-full text-left px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm text-foreground transition-colors"
              >
                How do I process a POS transaction?
              </button>
              <button
                onClick={() => onSendMessage('How do I view analytics?')}
                className="w-full text-left px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm text-foreground transition-colors"
              >
                How do I view analytics?
              </button>
            </div>
          </div>
        ) : (
          messages?.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${
                message?.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              {message?.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Bot" size={16} color="var(--color-primary)" />
                </div>
              )}
              
              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  message?.role === 'user' ?'bg-primary text-white' :'bg-muted text-foreground'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message?.content}</p>
                
                {/* Suggested Actions */}
                {message?.metadata?.suggestedActions && message?.metadata?.suggestedActions?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-medium opacity-80">Suggested Actions:</p>
                    {message?.metadata?.suggestedActions?.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleActionClick(action)}
                        className="w-full text-left px-3 py-2 bg-background/50 hover:bg-background rounded text-xs flex items-center gap-2 transition-colors"
                      >
                        <Icon name="ArrowRight" size={12} />
                        {action?.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Related Topics */}
                {message?.metadata?.relatedTopics && message?.metadata?.relatedTopics?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium opacity-80 mb-2">Related Topics:</p>
                    <div className="flex flex-wrap gap-2">
                      {message?.metadata?.relatedTopics?.map((topic, idx) => (
                        <button
                          key={idx}
                          onClick={() => onSendMessage(topic)}
                          className="px-2 py-1 bg-background/50 hover:bg-background rounded text-xs transition-colors"
                        >
                          {topic}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs opacity-60 mt-2">
                  {new Date(message?.created_at)?.toLocaleTimeString()}
                </p>
              </div>

              {message?.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={16} color="var(--color-accent)" />
                </div>
              )}
            </div>
          ))
        )}

        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Bot" size={16} color="var(--color-primary)" />
            </div>
            <div className="bg-muted rounded-lg p-3">
              <div className="flex gap-2">
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
      {/* Input Area */}
      <div className="border-t border-border p-4 bg-background">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e?.target?.value)}
            placeholder="Ask me anything..."
            disabled={loading}
            className="flex-1 px-4 py-2 bg-muted border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          />
          <Button
            type="submit"
            disabled={loading || !inputValue?.trim()}
            iconName="Send"
            size="sm"
          />
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;